import React, { useEffect, useRef, useState } from 'react';
import { createChart, ColorType, IChartApi, Time } from 'lightweight-charts';
import { RefreshCw, Maximize2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import ErrorBoundary from './ErrorBoundary';
import { FullScreenModal } from './ui/FullScreenModal';

interface OpenInterestData {
  openInterest: number;
  openInterestAmount: number;
  timestamp: number;
  exchangeName: string;
}

const fetchOpenInterest = async (symbol = 'BTC'): Promise<OpenInterestData[]> => {
  const response = await fetch(
    `https://open-api-v4.coinglass.com/api/futures/openInterest/ohlc-history?symbol=${symbol}&interval=4h`,
    {
      headers: {
        'accept': 'application/json',
        'CG-API-KEY': import.meta.env.VITE_COINGLASS_API_KEY || ''
      }
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch data');
  }

  const result = await response.json();
  return result.data;
};

const OpenInterestChart: React.FC = () => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const [isFullScreen, setIsFullScreen] = useState(false);

  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ['openInterest'],
    queryFn: () => fetchOpenInterest(),
    refetchInterval: 5 * 60 * 1000, // 5 minutes
  });

  useEffect(() => {
    if (!chartContainerRef.current || !data?.length) return;

    const chartData = data.map((item: OpenInterestData) => ({
      time: item.timestamp as Time,
      value: item.openInterest
    }));

    const handleResize = () => {
      if (chartRef.current) {
        chartRef.current.applyOptions({ 
          width: chartContainerRef.current?.clientWidth || 600 
        });
      }
    };

    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: '#9ca3af',
      },
      width: chartContainerRef.current.clientWidth,
      height: isFullScreen ? window.innerHeight - 200 : 300,
      grid: {
        vertLines: { color: '#27272a' },
        horzLines: { color: '#27272a' },
      },
    });

    chartRef.current = chart;

    const areaSeries = chart.addAreaSeries({
      lineColor: '#eab308',
      topColor: 'rgba(234, 179, 8, 0.3)',
      bottomColor: 'rgba(234, 179, 8, 0)',
      lineWidth: 2,
    });

    areaSeries.setData(chartData);
    chart.timeScale().fitContent();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
    };
  }, [data, isFullScreen]);

  const formatUSD = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: 2
    }).format(value);
  };

  const ChartContent = () => (
    <div className={`bg-zinc-900 rounded-lg border border-zinc-800 p-6 ${isFullScreen ? 'h-full' : ''}`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-white">Bitcoin Open Interest</h3>
          {data && data.length > 0 && (
            <div className="text-2xl font-bold text-white mt-1">
              {formatUSD(data[data.length - 1].openInterest)}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            className="p-2 text-gray-400 hover:text-yellow-400 transition-colors"
            aria-label="Refresh data"
          >
            <RefreshCw size={20} />
          </button>
          <button
            onClick={() => setIsFullScreen(prev => !prev)}
            className="p-2 text-gray-400 hover:text-yellow-400 transition-colors"
            aria-label="Toggle fullscreen"
          >
            <Maximize2 size={20} />
          </button>
        </div>
      </div>
      
      <div ref={chartContainerRef} className={isFullScreen ? 'h-[calc(100vh-200px)]' : 'h-[300px]'} />
      
      {data && data.length > 0 && (
        <div className="text-xs text-gray-400 mt-4">
          Last updated: {new Date(data[data.length - 1].timestamp).toLocaleString()}
        </div>
      )}
    </div>
  );

  if (error) return null;

  if (isLoading || !data) return null;

  return (
    <>
      {!isFullScreen && <ChartContent />}
      <FullScreenModal
        isOpen={isFullScreen}
        onClose={() => setIsFullScreen(false)}
        title="Bitcoin Open Interest"
      >
        <ChartContent />
      </FullScreenModal>
    </>
  );
};

const OpenInterestChartWithErrorBoundary: React.FC = () => (
  <ErrorBoundary>
    <OpenInterestChart />
  </ErrorBoundary>
);

export default OpenInterestChartWithErrorBoundary;